import questionnaireUtils from './questionnaireUtils';
import surveyUrls from './surveyUrls';

export type SurveyMatchTarget = {
  id?: string;
  primarySurveyId?: string | null;
  meta?: fhir.Meta;
};

export const getQuestionnairePrimarySurveyId = (
  survey: SurveyMatchTarget,
): string | null => {
  if (survey.primarySurveyId) {
    return survey.primarySurveyId;
  }
  return questionnaireUtils.getPrimarySurveyId(survey as fhir.Questionnaire);
};

export const getResponsePrimarySurveyId = (
  response: fhir.QuestionnaireResponse,
): string | null =>
  response?.meta?.tag?.find(tag => tag?.system === surveyUrls.primarySurveyId)
    ?.code ?? null;

export const isCurrentUserAuthor = (
  response: fhir.QuestionnaireResponse,
  currentUserId?: string | null,
): boolean => {
  if (!currentUserId) {
    return false;
  }
  const reference = response.author?.reference;
  if (!reference) {
    return false;
  }
  return (
    reference === currentUserId ||
    reference === `Practitioner/${currentUserId}` ||
    reference.endsWith(`/${currentUserId}`)
  );
};

const collectSurveyMatchIds = (survey: SurveyMatchTarget): Set<string> => {
  const ids = new Set<string>();
  if (survey.id) {
    ids.add(survey.id);
  }
  const primarySurveyId = getQuestionnairePrimarySurveyId(survey);
  if (primarySurveyId) {
    ids.add(primarySurveyId);
  }
  return ids;
};

const collectResponseMatchIds = (
  response: fhir.QuestionnaireResponse,
): Set<string> => {
  const ids = new Set<string>();
  const questionnaireId = questionnaireUtils.surveyIdFromResponse(response);
  if (questionnaireId) {
    ids.add(questionnaireId);
  }
  const primarySurveyId = getResponsePrimarySurveyId(response);
  if (primarySurveyId) {
    ids.add(primarySurveyId);
  }
  return ids;
};

export const responseMatchesSurvey = (
  response: fhir.QuestionnaireResponse,
  survey: SurveyMatchTarget,
): boolean => {
  const surveyIds = collectSurveyMatchIds(survey);
  if (surveyIds.size === 0) {
    return false;
  }
  for (const id of collectResponseMatchIds(response)) {
    if (surveyIds.has(id)) {
      return true;
    }
  }
  return false;
};

const authoredTimestamp = (response: fhir.QuestionnaireResponse): number => {
  const authored = response.authored ? Date.parse(response.authored) : NaN;
  return Number.isNaN(authored) ? 0 : authored;
};

export const getInProgressResponsesForSurvey = (
  responses: fhir.QuestionnaireResponse[] | undefined,
  survey: SurveyMatchTarget,
): fhir.QuestionnaireResponse[] =>
  (responses ?? [])
    .filter(
      response =>
        response.status === 'in-progress' &&
        responseMatchesSurvey(response, survey),
    )
    .sort((a, b) => authoredTimestamp(b) - authoredTimestamp(a));

export const getDefaultInProgressResponse = (
  inProgress: fhir.QuestionnaireResponse[],
  currentUserId?: string | null,
): fhir.QuestionnaireResponse | undefined => {
  const owned = inProgress.find(response =>
    isCurrentUserAuthor(response, currentUserId),
  );
  return owned ?? inProgress[0];
};
